import { useEffect, useRef, useState } from "react"
import { format, isBefore } from "date-fns"

import Icon from "@/components/icon/Icon"
import Search from "@/components/form/search/Search"
import BasicButton from "@/components/button/BasicButton"
import { useSearchActivityQuery } from "@/hooks/api/useActivityQuery"
import ActivitySearchItem from "@/app/activity/list/activitySearchItem/ActivitySearchItem"
import { createActivityCommonStore } from "@/app/activity/store"
import type { ActivitySearchItem as ActivitySearchItemType } from "@/types/activity"

interface ActivitySearchProps {
  isOpen: boolean
  onOpen?: () => void
  onClose?: () => void
}

export default function ActivitySearch({ isOpen, onOpen, onClose }: ActivitySearchProps) {
  if (!isOpen) {
    return (
      <button className="ActivityHeader__icon-btn" aria-label="Search" onClick={() => onOpen?.()}>
        <Icon type="search" />
      </button>
    )
  }

  return <ActiveSearch onClose={() => onClose?.()} />
}

function ActiveSearch({ onClose }: { onClose: () => void }) {
  const [searchValue, setSearchValue] = useState("")
  const containerRef = useRef<HTMLDivElement>(null)

  const { setCurrentDate, setInfoDialogActivity } = createActivityCommonStore((state) => ({
    setCurrentDate: state.setCurrentDate,
    setInfoDialogActivity: state.setInfoDialogActivity,
  }))

  const { data: searchQueryResult } = useSearchActivityQuery(searchValue)

  const searchResult = (() => {
    if (searchValue.length < 1) {
      return []
    }

    return (searchQueryResult ?? [])
      .filter(
        (item) =>
          item.name.toLowerCase().includes(searchValue.toLowerCase()) &&
          isBefore(item.dateStart, new Date()),
      )
      .sort((a, b) => (isBefore(a.dateStart, b.dateStart) ? 1 : -1))
      .slice(0, 5)
  })()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener("click", handleClickOutside, true)

    return () => document.removeEventListener("click", handleClickOutside, true)
  }, [onClose])

  const handleSelect = (item: ActivitySearchItemType) => {
    if (!item.idfActivity) {
      return
    }
    setCurrentDate(new Date(item.dateStart))
    setInfoDialogActivity(item)
    onClose()
  }

  return (
    <div className="ActivitySearch" ref={containerRef}>
      <div className="ActivitySearch__field">
        <Search onChange={setSearchValue} appendIcon={false} />
        {searchResult.length > 0 && (
          <div className="ActivitySearch__results">
            {searchResult.map((item) => (
              <ActivitySearchItem key={item.idfActivity} {...item} onClick={handleSelect} />
            ))}
          </div>
        )}
        {searchValue && searchResult.length === 0 && (
          <div className="ActivitySearch__results ActivitySearch__results--empty">
            <span>{`No matches for "${searchValue}"`}</span>
            <span className="ActivitySearch__results-date">{format(new Date(), "yyyy-MM-dd")}</span>
          </div>
        )}
      </div>
      <BasicButton className="ActivitySearch__close" type="ghost" size="sm" onClick={onClose}>
        <Icon type="close" />
      </BasicButton>
    </div>
  )
}
